import React, { useCallback, useState, useReducer, useEffect, useRef } from 'react';
import DescriptionLogicView from '../view/DescriptionLogicView';
import { UPDATE_FIELD } from '../../../store/modules/state/formBuilder';
import descriptionConditionsReducer from '../TextConditionsReducer';
import fetchAPI from '../../../utils/fetchAPI';
import dotProp from 'dot-prop-immutable';

// const fieldRules = [
//   {
//     description: 'mydescription1',
//     conditions: [
//       {
//         andOr: 'or',
//         input: 'f8005650674916704',
//         comparisons: [
//           { op: '=', value: 'Yes' },
//           { op: '=', value: 'Yes' }
//         ]
//       }
//     ],
//     andOr: 'and' // will always be and - not implemenet or functiomality
//   },
//   {
//     description: 'mydescription2',
//     conditions: [
//       {
//         andOr: 'or',
//         input: 'f2452138103119920',
//         comparisons: [{ op: '<>', value: '' }]
//       }
//     ],
//     andOr: 'and'
//   }
// ];

const DescriptionLogicEngine = props => {
  // reducer
  const [conditions, conditionsDispatch] = useReducer(
    descriptionConditionsReducer,
    []
  );

  // props
  const { rules, setRules, setDescription: setSimpleDescription } = props;

  // hooks
  const [useMultiDescription, setUseMultiDescription] = useState(false);
  const [ignoreUpdate, setIgnoreUpdate] = useState(true);
  const [ruleId, setRuleId] = useState(null);

  // onStateChanged
  useEffect(() => {
    conditions.length && updateSimpleDescription(conditions[0].description);

    if (ignoreUpdate) {
      setIgnoreUpdate(false);
      return;
    }

    if (!conditions.length) {
      updateSimpleDescription('');
      setUseMultiDescription(false);
    }

    !cleanLogic() && saveLogic();
  }, [conditions]);

  const saveLogic = () => {
    if (!useMultiDescription) {
      // logic is not being used - shouldn't send anything to server
      return;
    }

    const ruleIndex = ruleId ? rules.findIndex(rule => rule.id === ruleId) : -1;

    let methodType,
      fetchUrl = '/forms/v1/rules';
    const body = {
      formId: props.formId,
      fieldId: props.internalId,
      condition: JSON.stringify(conditions),
      action: {
        actionType: 'description',
        parameters: [
          {
            key: 'description',
            value: props.internalId
          }
        ]
      }
    };

    if (ruleId) {
      methodType = 'PUT';
      body.id = ruleId;
      fetchUrl += `/${ruleId}`;
      // show loader and block untill we get an it
    } else {
      methodType = 'POST';
    }

    fetchAPI(fetchUrl, {
      method: methodType,
      body,
      handleResponse: response =>
        response
          .text()
          .then(text => (text ? JSON.parse(text) : {}))
          .then(json => {
            if (response.ok) {
              return response;
            } else {
              return Promise.reject({
                response: response,
                json
              });
            }
          })
          .then(response => {
            if (!ruleId) {
              // get ID from Location header
              const id = response.headers.map.location;
              setRuleId(id);
              body.id = id; // in order to set rules
              // remove blocking animation
            }
          })
    });

    // update local rules for the next time the drawer will open
    const index = ruleIndex >= 0 ? ruleIndex : rules.length;
    setRules(dotProp.set(rules, index, body));
  };

  /**
   * Disable logic menu if it's not needed, this will happen if there is one description with no conditions
   * Will return true if logic got cleaned - false otherwise
   */
  const cleanLogic = () => {
    // const conditionIsPresent = ({ andOr, input, comparisons }) =>
    //   andOr || input || comparisons;

    const removeLogic = () => {
      setUseMultiDescription(false);

      if (ruleId) {
        // delete rules from server
        fetchAPI(`/forms/v1/rules/${ruleId}`, {
          method: 'DELETE'
        });
        setRuleId(null);

        const ruleIndex = rules.findIndex(rule => rule.id === ruleId);
        rules.splice(ruleIndex, 1);
        setRules([].concat(rules));
      }
    };

    let isLogicCleaned = false;

    if (!conditions.length) {
      removeLogic();
      isLogicCleaned = true;
    }
    if (conditions.length === 1) {
      let descriptionConditions = conditions[0].conditions;
      if (
        !descriptionConditions.length //||
        // !descriptionConditions.some(conditionIsPresent)
      ) {
        removeLogic();
        isLogicCleaned = true;
      }
    }

    return isLogicCleaned;
  };

  const unPackConditions = rules => {
    let _conditions = [],
      rule,
      ruleId;

    for (let i = 0; i < rules.length; i++) {
      if (
        rules[i].action.actionType === 'description' &&
        rules[i].fieldId === props.internalId
      ) {
        rule = rules[i];
        _conditions = JSON.parse(rule.condition);
        ruleId = rule.id;
        break;
      }
    }

    return [_conditions, ruleId];
  };

  useEffect(() => {
    const init = () => {
      let [_conditions, ruleId] = unPackConditions(rules);
      ruleId && setRuleId(ruleId);

      if (!_conditions.length && props.description) {
        // if (!ruleId && props.description) {
        // no logic on rules - use given description
        conditionsDispatch({
          type: 'addText',
          payload: { description: props.description }
        });
      } else if (_conditions.length) {
        // } else if (ruleId) {
        // load conditions to state
        setIgnoreUpdate(true);
        conditionsDispatch({ type: 'load', payload: _conditions });
      }

      setUseMultiDescription(Boolean(_conditions.length));
    };
    init();
  }, []);

  const cooldownThrotteling = useRef(0);

  const addCondition = () => {
    cooldownThrotteling.current++;
    setTimeout(() => {
      cooldownThrotteling.current--;
    }, 500);

    if (cooldownThrotteling.current > 1) {
      return;
    }

    // TODO: refactor this iffi if
    if (!useMultiDescription) {
      setUseMultiDescription(true);
      if (conditions[0]) {
        if (!conditions[0].conditions.length) {
          conditionsDispatch({
            type: 'addCondition',
            textNodeIndex: 0
          });
        }
        return;
      }
    }
    conditionsDispatch({
      type: 'addText',
      payload: { description: '' }
    });
  };

  const updateSimpleDescription = descriptionText => {
    props.autosaveField(UPDATE_FIELD, {
      field: { description: descriptionText }
    });
    setSimpleDescription(descriptionText);
  };

  const onDescriptionChange = (event, descriptionIndex, isFocused) => {
    const descriptionText = event.target.value;

    if (isFocused) {
      setIgnoreUpdate(true);
    }
    let dispatchType = conditions.length ? 'updateText' : 'addText';
    conditionsDispatch({
      type: dispatchType,
      textNodeIndex: descriptionIndex,
      payload: { description: descriptionText }
    });
  };

  const emojiCallback = useCallback(
    value => {
      onDescriptionChange({target: {value: value}}, 0, true);
    },
    [onDescriptionChange]
  );

  const viewProps = {
    useMultiDescription,
    simpleDescription: (conditions[0] && conditions[0].description) || '',
    addCondition,
    conditions,
    conditionsDispatch,
    onDescriptionChange,
    emojiCallback,
    ...props
  };

  return <DescriptionLogicView {...viewProps} />;
};

export default DescriptionLogicEngine;
